import { Search, X } from "lucide-react";
import { useId, useRef, useState, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { Kbd } from "./kbd";

export type SearchFieldProps = {
  label: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Shown as a hint key on the right while the field is empty. */
  shortcut?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "value" | "defaultValue" | "onChange">;

export function SearchField({
  label,
  value,
  defaultValue = "",
  onValueChange,
  shortcut,
  placeholder = "Search",
  id,
  className,
  disabled,
  onKeyDown,
  ...props
}: SearchFieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const input = useRef<HTMLInputElement>(null);
  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  const current = value ?? uncontrolled;

  function update(next: string) {
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  }

  return (
    <div
      data-slot="search-field"
      className={cn(
        "flex h-11 items-center gap-2 rounded-control border border-line bg-surface pl-3.5 focus-within:border-[var(--focus-outline)] focus-within:ring-1 focus-within:ring-[var(--focus-outline)]",
        disabled && "bg-surface-muted",
        className,
      )}
    >
      <label htmlFor={fieldId} className="sr-only">
        {label}
      </label>
      <Search aria-hidden className="size-4 shrink-0 text-muted" strokeWidth={1.75} />
      <input
        ref={input}
        id={fieldId}
        type="search"
        role="searchbox"
        disabled={disabled}
        placeholder={placeholder}
        value={current}
        data-field="true"
        onChange={(event) => update(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && current) {
            event.preventDefault();
            update("");
          }
          onKeyDown?.(event);
        }}
        className="h-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted disabled:cursor-not-allowed [&::-webkit-search-cancel-button]:hidden"
        {...props}
      />
      {current ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            update("");
            input.current?.focus();
          }}
          className="grid size-11 shrink-0 place-items-center rounded-control text-secondary can-hover:hover:text-foreground"
        >
          <X aria-hidden className="size-4" strokeWidth={1.75} />
        </button>
      ) : shortcut ? (
        <Kbd className="mr-3">{shortcut}</Kbd>
      ) : (
        <span className="w-1.5" />
      )}
    </div>
  );
}
